import React, { useLayoutEffect, useRef } from "react"

interface HomeUnitFrameProps {
  id: string
  /**
   * Reports the content's natural height (px) for a widget that sizes to its
   * content, and whether it drew anything at all.
   */
  onMeasure: (id: string, px: number, empty: boolean) => void
  children: React.ReactNode
}

/**
 * Measures a widget that sizes to its content — the priority actions, whose
 * entries wrap onto more rows as the widget narrows — so its grid cell is
 * exactly as tall as what it shows. A fixed height would clip the wrapped
 * entries or leave blank space under them.
 */
export const HomeUnitFrame = ({
  id,
  onMeasure,
  children
}: HomeUnitFrameProps) => {
  const ref = useRef<HTMLDivElement | null>(null)

  useLayoutEffect(() => {
    const element = ref.current
    if (!element) return
    // The widget fills its cell, so its own box only reads back the height
    // it was given. Its natural height is its header plus its body's
    // content, which do not depend on the cell — so the cell can be set to
    // exactly that, and the widget still fills it edge to edge. Emptiness is
    // read from what was drawn, not from a height of zero, which also means
    // "not laid out yet".
    const parts = () => {
      const card = element.firstElementChild as HTMLElement | null
      const header = card?.querySelector<HTMLElement>(
        ":scope > .home-card__header"
      )
      const body = card?.querySelector<HTMLElement>(":scope > .home-card__body")
      const content = body?.firstElementChild as HTMLElement | null
      return { card, header, body, content }
    }
    const report = () => {
      const { card, header, body, content } = parts()
      let px = card?.offsetHeight ?? 0
      if (card && header && body && content) {
        const style = getComputedStyle(body)
        const borders =
          parseFloat(getComputedStyle(card).borderTopWidth) +
          parseFloat(getComputedStyle(card).borderBottomWidth)
        px =
          header.offsetHeight +
          content.offsetHeight +
          parseFloat(style.paddingTop) +
          parseFloat(style.paddingBottom) +
          borders
      }
      onMeasure(id, px, element.childElementCount === 0)
    }
    report()
    if (typeof ResizeObserver !== "function") return
    const observer = new ResizeObserver(report)
    const { header, content } = parts()
    if (header) observer.observe(header)
    if (content) observer.observe(content)
    return () => observer.disconnect()
  }, [id, onMeasure])

  return (
    <div ref={ref} className="home-unit-frame" data-testid={`home-unit-${id}`}>
      {children}
    </div>
  )
}
